import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import {
  Cameraswitch,
  Close,
  FiberManualRecord,
  LockOutlined,
  Replay,
  Stop,
  Videocam,
  VideocamOff
} from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { MAX_VIDEO_MB, MAX_VIDEO_SECONDS } from 'components/play/admin/mediaLimits';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';

const EASE = 'cubic-bezier(0.32,0.72,0,1)';

/**
 * Contenedores que sabe grabar cada navegador, de mejor a peor. Safari solo produce MP4;
 * Chrome/Edge modernos también, y los viejos caen a WebM (VP9 → VP8); Firefox siempre WebM.
 * El primero que pase `isTypeSupported` es el que se usa.
 */
const MIME_CANDIDATES = [
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm'
];

const pickMimeType = () => {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return '';
  return (
    MIME_CANDIDATES.find((type) => {
      try {
        return window.MediaRecorder.isTypeSupported(type);
      } catch {
        return false;
      }
    }) || ''
  );
};

const extensionFor = (mimeType) => (mimeType.includes('mp4') ? 'mp4' : 'webm');

/**
 * iPadOS 13+ se anuncia como "Macintosh": el único indicio fiable de que es un táctil es
 * `maxTouchPoints`. La distinción importa porque en móvil se gira con `facingMode` (frontal
 * o trasera) y en escritorio ciclando por `deviceId`.
 */
const detectMobile = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return iPadOS || /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
};

const classifyError = (error) => {
  const name = error?.name || '';
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return 'denied';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return 'notFound';
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') return 'busy';
  return 'generic';
};

const formatClock = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const stageSx = {
  position: 'relative',
  width: '100%',
  aspectRatio: { xs: '3 / 4', sm: '16 / 9' },
  minHeight: 200,
  borderRadius: '14px',
  overflow: 'hidden',
  backgroundColor: 'ink.main',
  border: '1px solid',
  borderColor: 'hairline.main'
};

// `contain` y no `cover`: el encuadre que se ve es EXACTAMENTE el que queda grabado.
const mediaSx = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' };

const overlaySx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: 1,
  px: 3,
  color: 'common.white'
};

// Botón secundario del pie: gris con foco dorado, nunca compite con el CTA blanco.
const secondaryButtonSx = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: 2.5,
  py: 1.2,
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'hairline.main',
  transition: `color 200ms ${EASE}, border-color 200ms ${EASE}, transform 200ms ${EASE}`,
  '&:hover': { color: 'gold.main', borderColor: 'hairline.strong' },
  '&:active': { transform: 'scale(0.98)' }
};

const flipButtonSx = {
  flex: 'none',
  width: 46,
  height: 46,
  color: 'text.secondary',
  border: '1px solid',
  borderColor: 'hairline.main',
  transition: `color 200ms ${EASE}, border-color 200ms ${EASE}, transform 200ms ${EASE}`,
  '&:hover': { color: 'gold.main', borderColor: 'hairline.strong' },
  '&:active': { transform: 'scale(0.98)' },
  '&.Mui-disabled': { color: 'text.disabled', borderColor: 'hairline.main' }
};

/**
 * OTRIX Play (admin) — grabadora de video DENTRO de la app: pide permisos, muestra el preview
 * en vivo, graba con `MediaRecorder`, deja repetir la toma y devuelve el archivo listo para la
 * misma validación y subida de siempre.
 *
 * Dos restricciones del navegador mandan sobre todo lo demás:
 * 1. `getUserMedia` solo existe en https o en localhost; fuera de ahí se explica y se ofrece subir archivo.
 * 2. La cámara se suelta SIEMPRE (al detener, al cerrar y al desmontar): un track vivo deja el
 *    LED encendido y bloquea la cámara para el resto del sistema.
 */
function VideoRecorderModal({ open, onClose, onRecorded, title = null }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();

  const isMobile = useRef(detectMobile()).current;

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef('');
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const previewUrlRef = useRef(null);
  const discardRef = useRef(false);
  const autoStoppedRef = useRef(false);
  // Invalida las peticiones de cámara en vuelo: si el modal se cierra mientras el navegador
  // resuelve el permiso, el stream que llegue tarde se apaga en vez de quedar encendido.
  const runIdRef = useRef(0);

  const [phase, setPhase] = useState('starting');
  const [errorKind, setErrorKind] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [cameras, setCameras] = useState([]);
  const [facing, setFacing] = useState(isMobile ? 'environment' : 'user');
  const facingRef = useRef(facing);
  const [deviceIndex, setDeviceIndex] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [recordedFile, setRecordedFile] = useState(null);
  const [noAudio, setNoAudio] = useState(false);

  const notify = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `rec-cam-${Date.now()}`, variant, message })));

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const revokePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const openCamera = ({ deviceId, withAudio }) =>
    navigator.mediaDevices.getUserMedia({
      audio: withAudio,
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: facing } })
      }
    });

  const tryOpen = async (attempt) => {
    try {
      return { stream: await openCamera(attempt) };
    } catch (error) {
      return { error };
    }
  };

  /** Pide (o vuelve a pedir) la cámara. `deviceId` null = deja mandar al `facingMode`. */
  const start = async ({ deviceId }) => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setErrorKind(window.isSecureContext === false ? 'insecure' : 'unsupported');
      setPhase('error');
      return;
    }
    if (typeof window.MediaRecorder === 'undefined') {
      setErrorKind('unsupported');
      setPhase('error');
      return;
    }

    runIdRef.current += 1;
    const runId = runIdRef.current;

    setPhase('starting');
    setErrorKind(null);
    // La cámara anterior se suelta ANTES de pedir la nueva: muchos teléfonos no abren las dos.
    stopTracks();

    let result = await tryOpen({ deviceId, withAudio: true });
    // Una cámara concreta puede haber desaparecido (USB desconectado): se reintenta con la que haya.
    if (!result.stream && deviceId) result = await tryOpen({ deviceId: null, withAudio: true });

    let silent = false;
    // Sin micrófono el navegador rechaza la petición COMPLETA, incluso teniendo cámara.
    if (!result.stream && classifyError(result.error) !== 'denied') {
      const silentResult = await tryOpen({ deviceId: null, withAudio: false });
      if (silentResult.stream) {
        result = silentResult;
        silent = true;
      }
    }

    if (runIdRef.current !== runId) {
      if (result.stream) result.stream.getTracks().forEach((track) => track.stop());
      return;
    }

    if (!result.stream) {
      setErrorKind(classifyError(result.error));
      setPhase('error');
      return;
    }

    streamRef.current = result.stream;
    setNoAudio(silent || result.stream.getAudioTracks().length === 0);

    if (videoRef.current) {
      videoRef.current.srcObject = result.stream;
      // iOS no arranca el preview solo aunque el elemento sea `autoPlay`.
      const played = videoRef.current.play();
      if (played?.catch) played.catch(() => {});
    }

    setPhase('live');
    setElapsed(0);

    // Las etiquetas y los ids solo existen DESPUÉS de conceder el permiso.
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (runIdRef.current === runId) setCameras(devices.filter((device) => device.kind === 'videoinput'));
    } catch {
      /* Enumerar es opcional: sin lista simplemente no se ofrece girar la cámara. */
    }
  };

  const teardown = () => {
    runIdRef.current += 1;
    discardRef.current = true;
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* El navegador ya lo había cerrado. */
      }
    }
    recorderRef.current = null;
    chunksRef.current = [];
    stopTracks();
    revokePreview();
  };

  // `start` y `teardown` leen estado de este render; los efectos de abajo solo pueden depender
  // de `open`/`facing`, así que se llaman siempre a través de la versión más reciente.
  const latestRef = useRef({ start: null, teardown: null });
  useEffect(() => {
    latestRef.current = { start, teardown };
  });

  useEffect(() => {
    if (!open) return undefined;
    discardRef.current = false;
    autoStoppedRef.current = false;
    setPreviewUrl(null);
    setRecordedFile(null);
    setElapsed(0);
    latestRef.current.start({ deviceId: null });
    return () => latestRef.current.teardown();
  }, [open]);

  const handleRecorderStop = () => {
    clearTimer();
    const chunks = chunksRef.current;
    chunksRef.current = [];
    // La cámara se apaga apenas termina la toma: nada de LED encendido durante la revisión.
    stopTracks();
    if (discardRef.current) return;

    const type = mimeRef.current || 'video/webm';
    const blob = new Blob(chunks, { type });
    if (!blob.size) {
      setErrorKind('generic');
      setPhase('error');
      return;
    }

    revokePreview();
    const url = URL.createObjectURL(blob);
    previewUrlRef.current = url;
    setPreviewUrl(url);

    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    setRecordedFile(new File([blob], `otrix-video-${stamp}.${extensionFor(type)}`, { type }));
    setPhase('review');

    if (autoStoppedRef.current) {
      autoStoppedRef.current = false;
      notify(
        'warning',
        t('recoveryAdmin.recorder.autoStopped', {
          ns: 'play',
          minutes: Math.round(MAX_VIDEO_SECONDS / 60),
          defaultValue: 'Llegaste al máximo de {{minutes}} minutos, así que cortamos ahí la grabación.'
        })
      );
    }
  };

  const stopRecording = () => {
    clearTimer();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try {
        recorderRef.current.stop();
      } catch {
        /* El navegador ya lo había cerrado. */
      }
    }
  };

  const handleStartRecording = () => {
    if (!streamRef.current) return;
    const mimeType = pickMimeType();
    mimeRef.current = mimeType;
    chunksRef.current = [];
    discardRef.current = false;
    autoStoppedRef.current = false;

    let recorder;
    try {
      recorder = mimeType
        ? new window.MediaRecorder(streamRef.current, { mimeType })
        : new window.MediaRecorder(streamRef.current);
    } catch {
      setErrorKind('unsupported');
      setPhase('error');
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = handleRecorderStop;
    recorderRef.current = recorder;
    // Trozos de un segundo: si el navegador se cae no se pierde la toma entera.
    recorder.start(1000);

    startedAtRef.current = Date.now();
    setElapsed(0);
    setPhase('recording');
    timerRef.current = window.setInterval(() => {
      const seconds = Math.floor((Date.now() - startedAtRef.current) / 1000);
      setElapsed(seconds);
      if (seconds >= MAX_VIDEO_SECONDS) {
        autoStoppedRef.current = true;
        stopRecording();
      }
    }, 250);
  };

  const handleFlip = () => {
    if (cameras.length < 2) return;
    if (isMobile) {
      setFacing((current) => (current === 'user' ? 'environment' : 'user'));
      return;
    }
    const nextIndex = (deviceIndex + 1) % cameras.length;
    setDeviceIndex(nextIndex);
    start({ deviceId: cameras[nextIndex]?.deviceId || null });
  };

  // En móvil el giro se pide por `facingMode`, así que el stream se rehace cuando cambia.
  useEffect(() => {
    if (!open || facingRef.current === facing) return;
    facingRef.current = facing;
    latestRef.current.start({ deviceId: null });
  }, [facing, open]);

  const handleRetake = () => {
    revokePreview();
    setPreviewUrl(null);
    setRecordedFile(null);
    setElapsed(0);
    start({ deviceId: isMobile ? null : cameras[deviceIndex]?.deviceId || null });
  };

  const handleUse = () => {
    if (recordedFile) onRecorded(recordedFile);
    teardown();
    onClose();
  };

  const handleClose = () => {
    teardown();
    onClose();
  };

  const errorCopy = {
    denied: {
      icon: <LockOutlined sx={{ fontSize: 34, color: 'gold.main' }} />,
      title: t('recoveryAdmin.recorder.deniedTitle', { ns: 'play', defaultValue: 'Cámara bloqueada' }),
      body: t('recoveryAdmin.recorder.deniedBody', {
        ns: 'play',
        defaultValue:
          'El navegador bloqueó la cámara para este sitio. Abre el candado de la barra de direcciones, permite cámara y micrófono, y vuelve a intentar.'
      })
    },
    notFound: {
      icon: <VideocamOff sx={{ fontSize: 34, color: 'gold.main' }} />,
      title: t('recoveryAdmin.recorder.notFoundTitle', { ns: 'play', defaultValue: 'No encontramos ninguna cámara' }),
      body: t('recoveryAdmin.recorder.notFoundBody', {
        ns: 'play',
        defaultValue: 'Conecta una cámara o cierra esta ventana y usa "Subir archivo" con un video ya grabado.'
      })
    },
    busy: {
      icon: <VideocamOff sx={{ fontSize: 34, color: 'gold.main' }} />,
      title: t('recoveryAdmin.recorder.busyTitle', { ns: 'play', defaultValue: 'La cámara está ocupada' }),
      body: t('recoveryAdmin.recorder.busyBody', {
        ns: 'play',
        defaultValue: 'Otra aplicación la está usando. Ciérrala y vuelve a intentar.'
      })
    },
    insecure: {
      icon: <LockOutlined sx={{ fontSize: 34, color: 'gold.main' }} />,
      title: t('recoveryAdmin.recorder.insecureTitle', { ns: 'play', defaultValue: 'Conexión no segura' }),
      body: t('recoveryAdmin.recorder.insecureBody', {
        ns: 'play',
        defaultValue: 'Los navegadores solo dan acceso a la cámara por https. Entra por https o usa "Subir archivo".'
      })
    },
    unsupported: {
      icon: <VideocamOff sx={{ fontSize: 34, color: 'gold.main' }} />,
      title: t('recoveryAdmin.recorder.unsupportedTitle', { ns: 'play', defaultValue: 'Este navegador no puede grabar' }),
      body: t('recoveryAdmin.recorder.unsupportedBody', {
        ns: 'play',
        defaultValue: 'Prueba con Chrome o Safari actualizado, o usa "Subir archivo" con un video ya grabado.'
      })
    },
    generic: {
      icon: <VideocamOff sx={{ fontSize: 34, color: 'gold.main' }} />,
      title: t('recoveryAdmin.recorder.genericTitle', { ns: 'play', defaultValue: 'No pudimos abrir la cámara' }),
      body: t('recoveryAdmin.recorder.genericBody', {
        ns: 'play',
        defaultValue: 'Vuelve a intentar; si sigue igual, usa "Subir archivo".'
      })
    }
  };

  const activeError = errorKind ? errorCopy[errorKind] || errorCopy.generic : null;
  const canRetry = errorKind !== 'insecure' && errorKind !== 'unsupported';
  const canFlip = cameras.length > 1 && (phase === 'live' || phase === 'starting');
  // En escritorio la cámara se ve en espejo (como en cualquier videollamada); en móvil solo la frontal.
  const mirrored = !isMobile || facing === 'user';
  const recordedMb = recordedFile ? recordedFile.size / (1024 * 1024) : 0;
  const isOverSize = recordedMb > MAX_VIDEO_MB;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="otx-video-recorder-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Videocam sx={{ color: 'gold.main', flex: 'none' }} />
          <Typography id="otx-video-recorder-title" variant="h6" fontWeight={800} letterSpacing="-0.02em" noWrap>
            {title || t('recoveryAdmin.recorder.title', { ns: 'play', defaultValue: 'Grabar video' })}
          </Typography>
        </Stack>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ flex: 'none', color: 'text.secondary' }}
          aria-label={t('recoveryAdmin.recorder.close', { ns: 'play', defaultValue: 'Cerrar' })}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
        <Stack spacing={2}>
          <Box sx={stageSx}>
            <Box
              component="video"
              ref={videoRef}
              autoPlay
              muted
              playsInline
              sx={{ ...mediaSx, transform: mirrored ? 'scaleX(-1)' : 'none' }}
            />

            {/* La toma grabada se monta ENCIMA del preview en vivo (que para entonces ya está
                apagado y en negro), así el elemento de la cámara nunca se oculta: iOS no
                arranca la reproducción de un `video` con `display: none`. */}
            {phase === 'review' && previewUrl && (
              <Box component="video" src={previewUrl} controls playsInline sx={mediaSx} />
            )}

            {phase === 'starting' && (
              <Box sx={overlaySx}>
                <CircularProgress size={22} sx={{ color: 'gold.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.8 }}>
                  {t('recoveryAdmin.recorder.requesting', {
                    ns: 'play',
                    defaultValue: 'Pidiendo acceso a la cámara…'
                  })}
                </Typography>
              </Box>
            )}

            {phase === 'error' && activeError && (
              <Box sx={overlaySx}>
                {activeError.icon}
                <Typography variant="subtitle2" fontWeight={800}>
                  {activeError.title}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75, maxWidth: 380, lineHeight: 1.5 }}>
                  {activeError.body}
                </Typography>
              </Box>
            )}

            {phase === 'recording' && (
              <>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    color: 'common.white',
                    backgroundColor: (theme) => alpha(theme.palette.ink.main, 0.72)
                  }}>
                  <FiberManualRecord
                    sx={{
                      fontSize: 10,
                      color: 'sessionStatus.absent.fg',
                      animation: 'otxRecPulse 1.2s ease-in-out infinite',
                      '@keyframes otxRecPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
                    }}
                  />
                  <Typography variant="caption" fontWeight={800} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {`${formatClock(elapsed)} / ${formatClock(MAX_VIDEO_SECONDS)}`}
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 3,
                    backgroundColor: (theme) => alpha(theme.palette.ink.main, 0.5)
                  }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${Math.min(100, (elapsed / MAX_VIDEO_SECONDS) * 100)}%`,
                      backgroundColor: 'gold.main',
                      transition: `width 250ms ${EASE}`
                    }}
                  />
                </Box>
              </>
            )}
          </Box>

          {phase === 'error' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              {canRetry && (
                <Button
                  fullWidth
                  onClick={() => start({ deviceId: null })}
                  sx={{ ...premiumButtonSx, py: 1.2, flex: 1 }}>
                  {t('recoveryAdmin.recorder.retry', { ns: 'play', defaultValue: 'Reintentar' })}
                </Button>
              )}
              <Button fullWidth onClick={handleClose} sx={{ ...secondaryButtonSx, flex: 1 }}>
                {t('recoveryAdmin.recorder.close', { ns: 'play', defaultValue: 'Cerrar' })}
              </Button>
            </Stack>
          ) : null}

          {(phase === 'starting' || phase === 'live' || phase === 'recording') && (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <IconButton
                onClick={handleFlip}
                disabled={!canFlip}
                aria-label={t('recoveryAdmin.recorder.flip', { ns: 'play', defaultValue: 'Cambiar de cámara' })}
                sx={flipButtonSx}>
                <Cameraswitch fontSize="small" />
              </IconButton>

              {phase === 'recording' ? (
                <Button
                  fullWidth
                  onClick={stopRecording}
                  startIcon={<Stop sx={{ fontSize: 18 }} />}
                  sx={{ ...premiumButtonSx, py: 1.2 }}>
                  {t('recoveryAdmin.recorder.stop', { ns: 'play', defaultValue: 'Detener' })}
                </Button>
              ) : (
                <Button
                  fullWidth
                  onClick={handleStartRecording}
                  disabled={phase !== 'live'}
                  startIcon={<FiberManualRecord sx={{ fontSize: 14, color: 'sessionStatus.absent.fg' }} />}
                  sx={{ ...premiumButtonSx, py: 1.2 }}>
                  {t('recoveryAdmin.recorder.start', { ns: 'play', defaultValue: 'Grabar' })}
                </Button>
              )}
            </Stack>
          )}

          {phase === 'review' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <Button
                fullWidth
                onClick={handleRetake}
                startIcon={<Replay sx={{ fontSize: 18 }} />}
                sx={{ ...secondaryButtonSx, flex: 1 }}>
                {t('recoveryAdmin.recorder.retake', { ns: 'play', defaultValue: 'Repetir' })}
              </Button>
              <Button
                fullWidth
                onClick={handleUse}
                disabled={isOverSize}
                sx={{ ...premiumButtonSx, py: 1.2, flex: 1 }}>
                {t('recoveryAdmin.recorder.use', { ns: 'play', defaultValue: 'Usar este video' })}
              </Button>
            </Stack>
          )}

          <Stack spacing={0.25}>
            {phase === 'review' && (
              <Typography
                variant="caption"
                sx={{
                  color: isOverSize ? 'sessionStatus.absent.fg' : 'text.secondary',
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums'
                }}>
                {isOverSize
                  ? t('recoveryAdmin.recorder.tooBig', {
                      ns: 'play',
                      mb: MAX_VIDEO_MB,
                      defaultValue: 'El clip pesa más de {{mb}} MB. Repite la toma más corta.'
                    })
                  : t('recoveryAdmin.recorder.recorded', {
                      ns: 'play',
                      time: formatClock(elapsed),
                      mb: recordedMb.toFixed(1),
                      defaultValue: 'Grabaste {{time}} ({{mb}} MB).'
                    })}
              </Typography>
            )}

            {noAudio && phase !== 'error' && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('recoveryAdmin.recorder.noAudio', {
                  ns: 'play',
                  defaultValue: 'No encontramos micrófono: el video se graba sin audio.'
                })}
              </Typography>
            )}

            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {t('recoveryAdmin.videoLimits', {
                ns: 'play',
                minutes: Math.round(MAX_VIDEO_SECONDS / 60),
                mb: MAX_VIDEO_MB,
                defaultValue: 'Máximo {{minutes}} minutos y {{mb}} MB.'
              })}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

VideoRecorderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRecorded: PropTypes.func.isRequired,
  // Esta versión de `require-default-props` todavía exige `defaultProps`, que React 18.3 ya
  // deprecó en componentes de función: el valor por defecto va en la firma.
  // eslint-disable-next-line react/require-default-props
  title: PropTypes.string
};

export default VideoRecorderModal;
